// 1. Define a type for the user object (customize with your actual user data)
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { IUser } from '@/interfaces/user.interface';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_NOTIFICATION_SERVICE } from '@/api/constant';

interface UserState {
  isAuthenticated: boolean;
  user: IUser | null;
  isSideBarOpen: number;
  isActiveTab: string;
  userWebsiteId: string;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  notificationCount: number;
}

const initialState: UserState = {
  isAuthenticated: false,
  user: null,
  isSideBarOpen: 0,
  isActiveTab: '',
  userWebsiteId: '',
  loading: 'idle',
  notificationCount: 0,
  error: null,
};

export const fetchNotificationCount = createAsyncThunk(
  'user/fetchNotificationCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `${API_URL_NOTIFICATION_SERVICE}${API_ENDPOINT.notification}/count`,
        { method: 'GET' },
      );

      return response?.count;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  },
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUserData: (state, action: PayloadAction<IUser | null>) => {
      state.user = action.payload;
    },
    setUserWebsiteId: (state, action: PayloadAction<string>) => {
      state.userWebsiteId = action.payload;
    },
    setSideBarOpen: (state, action: PayloadAction<number>) => {
      state.isSideBarOpen = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.isActiveTab = action.payload;
    },
    logout: (state) => {
      Object.assign(state, initialState);
      state.isAuthenticated = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCount.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(
        fetchNotificationCount.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = 'succeeded';
          state.notificationCount = action.payload;
        },
      )
      .addCase(fetchNotificationCount.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const {
  setAuthenticated,
  setUserData,
  setSideBarOpen,
  setActiveTab,
  logout,
  setUserWebsiteId,
} = userSlice.actions;

export const selectUserState = (state: RootState) => state.user;

export default userSlice.reducer;
